import { Form, Formik, FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import moment from 'moment';
import { useMemo } from 'react';
import { BsSearch } from 'react-icons/bs';
import { FaFire } from 'react-icons/fa';
import { sectionAnimVariants } from '../../../constants/animationVariants';
import {
  DEFAULT_TIME_PERIOD,
  SELECT_FIELD_DEFAULT_VALUE,
  TIME_PERIOD_DESCRIPTIONS,
  TIME_PERIOD_OPTIONS,
} from '../../../constants/settings';
import { useBookingContext } from '../../../contexts/BookingProvider';
import useAppointmentActions from '../../../hooks/APIHooks/appointments/useAppointmentActions';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import useLanguageActions from '../../../hooks/APIHooks/users/useLanguageActions';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { appointmentSearchSchema } from '../../../schemas';
import { extractTimeFromTimePeriod } from '../../../utils/timeAndDate';
import AcademiaButton from '../../CommonComps/Buttons/AcademiaButton';
import AcademiaDatePicker from '../../CommonComps/Inputs/AcademiaDatePicker';
import AcademiaFormSelect from '../../CommonComps/Inputs/AcademiaFormSelect';
import SectionHeader from '../../CommonComps/Titles/SectionHeader';

const SearchAppointmentsForm = () => {
  // integration of custom API hooks here
  const { getServiceListHandler } = useServiceActions('serviceList');
  const { getAddedLanguagesHandler } = useLanguageActions('addedLanguages');
  const { getProvinceListHandler } = useProvincesActions('provinceList');
  const { getAvailableAssociatesHandler } = useAppointmentActions('');

  // integration of context hooks here
  const {
    setIsLoading,
    setIsFetched,
    setSelectedDate,
    setServiceId,
    setTimePeriod,
    setAvailableAssociates,
    setGridViewData,
  } = useBookingContext();

  const serviceOptions = useMemo(() => {
    if (!getServiceListHandler.data?.data.length) return [];

    return getServiceListHandler.data.data.map((service) => ({
      id: service.id,
      name: service.name,
    }));
  }, [getServiceListHandler.data]);

  const serviceDescriptionOptions = useMemo(() => {
    if (!getServiceListHandler.data?.data.length) return [];

    return getServiceListHandler.data.data.map((service) =>
      service.is_most_popular ? (
        <FaFire title='Popular Right Now' className='text-base-orange' />
      ) : (
        <></>
      )
    );
  }, [getServiceListHandler.data]);

  const languageOptions = useMemo(() => {
    if (!getAddedLanguagesHandler.data?.data.length) return [];

    return [{ id: -1, name: '--Select--' }].concat(
      getAddedLanguagesHandler.data.data.map((lang) => ({
        id: lang.id,
        name: lang.name,
      }))
    );
  }, [getAddedLanguagesHandler.data]);

  const provinceOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.data.length) return [];

    return [{ id: -1, name: '--Select--' }].concat(
      getProvinceListHandler.data.data.map((province) => ({
        id: province.id,
        name: province.province_name,
      }))
    );
  }, [getProvinceListHandler.data]);

  const provinceDescriptionOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.data.length) return [];

    return [''].concat(
      getProvinceListHandler.data.data.map(
        (province) => `(${province.country_name})`
      )
    );
  }, [getProvinceListHandler.data]);

  // handler function to submit the search form declared here
  const searchFormSubmissionHandler = async (
    values: any,
    action: FormikHelpers<any>
  ) => {
    try {
      action.setSubmitting(true);

      console.log(values);

      setIsLoading(true);
      setIsFetched(false);

      let body = {
        service_id: values.service.id,
        selected_date: moment(values.date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        language: values.language.id !== -1 ? values.language.id : null,
      };

      if (values.time_period.id !== -1) {
        const timeFilters = extractTimeFromTimePeriod(values.time_period.name);

        setTimePeriod(timeFilters);
        body = { ...body, ...timeFilters };
      } else {
        setTimePeriod(DEFAULT_TIME_PERIOD);
      }

      await getAvailableAssociatesHandler
        .mutateAsync({
          body,
        })
        .then((res) => {
          if (res.data.is_success) {
            console.log('AVAILABLE ASSOCIATE LIST: ', res.data);

            for (const key in res.data.available_associates) {
              if (
                key === moment(values.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
              ) {
                setAvailableAssociates(res.data.available_associates[key]);
                break;
              }
            }

            setSelectedDate(values.date);
            setServiceId(values.service.id);
            setGridViewData(res.data);
          } else {
            setAvailableAssociates([]);
          }
        })
        .catch((error) => setAvailableAssociates([]));
    } catch (error) {
    } finally {
      setIsFetched(true);
      setIsLoading(false);
      action.setSubmitting(false);
    }
  };

  // rendering search appointments form component here
  return (
    <motion.section
      variants={sectionAnimVariants}
      initial='initial'
      animate='animate'
      className='bg-base-white shadow-academia-sm rounded-xl p-5'
    >
      <SectionHeader headerText='Search For Appointments' />

      <Formik
        initialValues={{
          service: SELECT_FIELD_DEFAULT_VALUE,
          date: '',
          time_period: SELECT_FIELD_DEFAULT_VALUE,
          language: SELECT_FIELD_DEFAULT_VALUE,
          province: SELECT_FIELD_DEFAULT_VALUE,
        }}
        validationSchema={appointmentSearchSchema}
        onSubmit={searchFormSubmissionHandler}
      >
        {(props) => (
          <Form className='space-y-7'>
            <div className='grid md:grid-cols-2 2xl:grid-cols-5 gap-4'>
              <AcademiaFormSelect
                name='service'
                label='Select Service'
                options={serviceOptions}
                defaultValue={props.values.service}
                optionDescription={serviceDescriptionOptions}
                showSearchInput
                isRequired
              />

              <AcademiaDatePicker
                label='Select Date'
                name='date'
                minDate={new Date()}
                placeholder='DD-MM-YYYY'
                isRequired
              />

              <AcademiaFormSelect
                name='time_period'
                label='Time Period'
                options={TIME_PERIOD_OPTIONS}
                optionDescription={TIME_PERIOD_DESCRIPTIONS}
                defaultValue={props.values.language}
                showSearchInput
              />

              <AcademiaFormSelect
                name='language'
                label='Select Language'
                options={languageOptions}
                defaultValue={props.values.language}
                showSearchInput
              />

              <AcademiaFormSelect
                name='province'
                label='Select Province'
                options={provinceOptions}
                optionDescription={provinceDescriptionOptions}
                defaultValue={props.values.province}
                showSearchInput
              />
            </div>

            <div className='flex items-center justify-center'>
              <AcademiaButton
                title={
                  props.values.service.id === -1
                    ? 'Please, select a service first!'
                    : undefined
                }
                type='submit'
                variant='premium'
                icon={<BsSearch />}
                additionalClassNames='w-full md:w-fit'
                isDisabled={props.values.service.id === -1}
              >
                Search
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </motion.section>
  );
};

export default SearchAppointmentsForm;
